import React, { useState, useMemo } from 'react'
import { Modal, notification, Spin } from 'antd'
import Papa from 'papaparse'
import { useTranslation } from 'react-i18next'
import gql from 'graphql-tag'
import { useCreateProvingMutation } from '../../views/ProvingsIndexView/ProvingsIndexView.generated'
import { ProvingInsertType, ProvingType } from '../../gql.generated'
import { useReadProvingGroundsQuery } from '../../views/AdminProvingGroundsView/AdminProvingGroundsView.generated'
import { useApolloClient, useMutation } from '@apollo/react-hooks'
import {
  UserSuggestionsDocument,
  UserSuggestionsQuery,
  UserSuggestionsQueryVariables,
} from '../../hooks/useUserSuggestions.generated'

// GraphQL mutation to trigger pending sync via existing updateProving resolver
const UPDATE_PROVING = gql`
  mutation UpdateProving($id: String!, $update: ProvingUpdateType!) {
    updateProving(id: $id, update: $update) {
      _id
    }
  }
`

export interface ImportProvingDialogProps {
  visible: boolean
  onClose: () => void
  /** Callback to refresh the provings list after a successful import */
  onImportSuccess?: () => void
}

export const ImportProvingDialog: React.FC<ImportProvingDialogProps> = ({
  visible,
  onClose,
  onImportSuccess,
}) => {
  const { t } = useTranslation('common')
  const [loading, setLoading] = useState(false)
  const [createProving] = useCreateProvingMutation()
  const [updateProving] = useMutation(UPDATE_PROVING)
  const client = useApolloClient()

  // Load proving grounds
  const { data: pgData, loading: pgLoading, error: pgError } = useReadProvingGroundsQuery()
  if (pgError) {
    notification.error({ message: t('importProving.loadErrorMessage'), description: pgError.message, duration: 0 })
  }

  // Preprocess proving grounds: strip documentation suffix and lowercase
  const groundMap = useMemo(() => {
    const map = new Map<string, any>();
    (pgData?.provingGrounds || []).forEach((g) => {
      const cleanTitle = g.title.replace(/\s*\(\s*(?:Nur(?: zur)? Dokumentation)\s*\)$/i, '').trim()
      map.set(cleanTitle.toLowerCase(), g)
    })
    return map
  }, [pgData]);

  const typeMap: Record<string, ProvingType> = {
    Fachbereich: ProvingType.Normal,
    'Syn. Fachbereich': ProvingType.Normal,
    Blockerprobung: ProvingType.Block,
    Dauerlauf: ProvingType.Continuous,
    'GE/VS': ProvingType.GeVs,
  }

  // Parse German date or ISO
  const parseDate = (value: string): Date => {
    const parts = value.trim().split('.')
    if (parts.length === 3) {
      const [d, m, y] = parts
      const iso = `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}T00:00:00Z`
      const dt = new Date(iso)
      if (isNaN(dt.getTime())) throw new Error(`Ungültiges Datum: "${value}"`)
      return dt
    }
    const dt = new Date(value)
    if (isNaN(dt.getTime())) throw new Error(`Ungültiges Datum: "${value}"`)
    return dt
  }

  const findUserWithRetry = async (filter: string) => {
    const MAX_ATTEMPTS = 3;
    let attempt = 0;
    let delay = 500;
    while (attempt < MAX_ATTEMPTS) {
      try {
        const { data: sug } = await client.query<UserSuggestionsQuery, UserSuggestionsQueryVariables>({
          query: UserSuggestionsDocument,
          variables: { filter },
          fetchPolicy: 'no-cache',
        });
        return sug.users?.filter(u => !!u) || [];
      } catch (err: any) {
        // retry on ETIMEDOUT
        if (err.message?.includes('ETIMEDOUT') && attempt < MAX_ATTEMPTS - 1) {
          await new Promise(res => setTimeout(res, delay));
          attempt++;
          delay *= 2;
          continue;
        }
        throw err;
      }
    }
    // if all attempts failed, throw generic
    throw new Error(`Timeout querying user for filter \"${filter}\" after ${MAX_ATTEMPTS} attempts`);
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file?.name.match(/\.csv$/i)) {
      notification.error({ message: t('importProving.invalidCsv'), duration: 0 })
      return
    }
    if (pgLoading) {
      notification.warning({ message: t('importProving.waitMessage'), description: t('importProving.waitDescription'), duration: 0 })
      return
    }

    setLoading(true)
    Papa.parse<Record<string,string>, File>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async ({ data }) => {
        const failures: string[] = []
        try {
          for (const row of data) {
            try {
              // 1) Proving ground extraction with robust normalization
              const raw = (row['Standort-Auswahl durch ESM'] || '').trim()
              // Try JSON
              let candidates: string[]
              try {
                const arr = JSON.parse(raw)
                candidates = Array.isArray(arr) ? arr.filter((e): e is string => typeof e === 'string') : [raw]
              } catch {
                candidates = raw.replace(/^[\["\]]+|[\["\]]+$/g, '').split(',').map(s => s.trim()).filter(Boolean)
              }
              // Clean parentheses and trailing codes
              const cleaned = candidates.map(c =>
                c.replace(/\s*\([^)]*\)/g, '')
                 .replace(/,\s*[^,()]+$/g, '')
                 .trim()
              )
              const name = (cleaned[0] || raw).toLowerCase()
              const ground = groundMap.get(name)
              if (!ground) throw new Error(`Unbekanntes Versuchsgelände: "${cleaned[0] || raw}"`)

              // 2) Type mapping
              const rawType = (row['Erprobungsart'] || '').trim()
              const typeEnum = typeMap[rawType]
              if (!typeEnum) throw new Error(`Unbekannte Erprobungsart: "${rawType}"`)

              // 3) Dates
              const dateFrom = parseDate(row['Von']).toISOString().split('T')[0]
              const dateTo = parseDate(row['Bis']).toISOString().split('T')[0]

              // 4) Contacts lookup
              const contactsRaw = (row['Ansprechpartner'] || '')
              const entries = contactsRaw.split(';').map(s => s.trim()).filter(Boolean)
              const responsible: string[] = []
              // ...existing code...
              for (const ent of entries) {
                const m = ent.match(/^([^,]+),\s*([^\(]+)/)
                if (!m) continue
                let filter = `${m[1].trim()}, ${m[2].trim()}`
                const dep = ent.match(/\(([^)]+)\)/)
                if (dep) filter += ` (${dep[1]})`
                const users = await findUserWithRetry(filter)
                if (users.length === 1) {
                  responsible.push(users[0]._id)
                } else if (users.length > 1) {
                  const porscheUsers = users.filter(u => u.email?.toLowerCase().includes('porsche'))
                  if (porscheUsers.length === 1) {
                    responsible.push(porscheUsers[0]._id)
                  } else {
                    throw new Error(`Fehler bei Graph-User für ${filter}: ${porscheUsers.length === 0 ? 'Keine eindeutige Porsche-Email gefunden' : 'Mehrere Porsche-Emails gefunden'}`)
                  }
                } else {
                  throw new Error(`Fehler bei Graph-User für ${filter}`)
                }
              }

              // 4b) Deputy lookup ("Vertretung Ansprechpartner")
              const deputyRaw = (row['Vertretung Ansprechpartner'] || '')
              const deputyEntries = deputyRaw.split(';').map(s => s.trim()).filter(Boolean)
              const deputy: string[] = []
              for (const ent of deputyEntries) {
                const m = ent.match(/^([^,]+),\s*([^\(]+)/)
                if (!m) continue
                let filter = `${m[1].trim()}, ${m[2].trim()}`
                const dep = ent.match(/\(([^)]+)\)/)
                if (dep) filter += ` (${dep[1]})`
                const users = await findUserWithRetry(filter)
                if (users.length === 1) {
                  deputy.push(users[0]._id)
                } else if (users.length > 1) {
                  const porscheUsers = users.filter(u => u.email?.toLowerCase().includes('porsche'))
                  if (porscheUsers.length === 1) {
                    deputy.push(porscheUsers[0]._id)
                  } else {
                    throw new Error(`Fehler bei Graph-User für Stellvertreter ${filter}: ${porscheUsers.length === 0 ? 'Keine eindeutige Porsche-Email gefunden' : 'Mehrere Porsche-Emails gefunden'}`)
                  }
                } else {
                  throw new Error(`Fehler bei Graph-User für Stellvertreter ${filter}`)
                }
              }

              // 5) Build input and create
              const cleanOrderId = (row['PSP'] || '').trim().replace(/\./g, '')
              const title = `${row['ERP-Titel']} (ID ${row['ID']})`
              const input: ProvingInsertType = {
                type: typeEnum,
                title,
                description: row['Grund & Inhalt'],
                dateFrom,
                dateTo,
                provingGroundId: ground._id,
                department: row['Fachbereich'],
                responsible,
                orderIdDistribution: [{ orderId: cleanOrderId, percentage: 100, annotation: '' }],
              }
              const res = await createProving({ variables: { input } })
              const newId = res.data?.createProving._id
              if (newId) await updateProving({ variables: { id: newId, update: { deputy } } })
            } catch (rowErr: any) {
              failures.push(`ID ${row['ID']}: ${rowErr.message}`)
            }
          }

          // 6) Notification
          if (failures.length === 0) {
            notification.success({ message: t('importProving.successMessage'), description: t('importProving.successDescription', { count: data.length }), duration: 0 })
            onImportSuccess?.(); onClose()
          } else if (failures.length < data.length) {
            const successCount = data.length - failures.length;
            notification.warning({
              message: t('importProving.partialSuccessMessage'),
              description: (
                <div>
                  {t('importProving.partialSuccessDescription', { count: successCount, total: data.length })}
                  <ul>{failures.map(f => <li key={f}>{f}</li>)}</ul>
                </div>
              ),
              duration: 0
            });
            onImportSuccess?.();
            onClose();
          } else {
            notification.error({ message: t('importProving.errorMessage'), description: failures.join('\n'), duration: 0 })
          }
        } catch (err: any) {
          notification.error({ message: t('importProving.errorMessage'), description: err.message, duration: 0 })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  return (
    <Modal title={t('importProving.modalTitle')} visible={visible} onCancel={onClose} footer={null}>
      {(pgLoading || loading) && <div style={{ textAlign: 'center', marginBottom: 16 }}><Spin tip={t('importProving.spinTip')} /></div>}
      <input type="file" accept=".csv" onChange={handleFile} disabled={loading || pgLoading} />
      <p>{t('importProving.chooseFileDescription')}</p>
    </Modal>
  )
}
